import { EndOwnershipReason, type UserGroupRecordListMinimal } from '@navio/nsdk';
import { useStore } from '@tanstack/react-form';
import { NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NavioListCard, NavioOverlayActions, NoddiInfoBanner, useAppForm } from 'noddi-ui-web';
import { TanstackUserGroupAsyncSelect } from '../../form/tanstack/features/Account/TanstackUserGroupAsyncSelect';
import { OwnershipDriverField } from './OwnershipDriverField';
import { OwnershipTransferChoices } from './OwnershipTransferChoices';
import { ownerCorrectionSchema, type OwnerCorrectionValues } from './ownershipTransferDraft';
import { useOwnershipRecipient } from './useOwnershipRecipient';

const FORM_ID = 'correct-owner-assignment-form';

type Effect = { icon: NoddiIconName; title: string; description: string };

const OWNER_EFFECT: Effect = {
  icon: 'UserCheck',
  title: 'The right customer becomes the only owner',
  description: 'Every other current owner is ended.'
};
const RECALLS_EFFECT: Effect = {
  icon: 'Bell',
  title: 'Open recalls move',
  description: 'Draft and pending recalls of the old owners.'
};
const TIRE_HOTEL_EFFECT: Effect = {
  icon: 'Warehouse',
  title: 'Tire hotel and stored wheels move',
  description: 'Also wheel sets already delivered. The wheels stay where they are, with their history.'
};
const NOTHING_SENT_EFFECT: Effect = {
  icon: 'CircleX',
  title: 'Nothing is sent or charged',
  description: 'No booking, payment or SMS.'
};

const END_REASON_OPTIONS = [
  {
    value: EndOwnershipReason.NEVER_OWNED,
    label: 'Registered on the wrong customer',
    description: 'The car was never theirs.',
    icon: 'User' as const
  },
  {
    value: EndOwnershipReason.SOLD,
    label: 'They no longer own it',
    description: 'They sold or handed over the car, and were never removed.',
    icon: 'ArrowLeftRight' as const
  }
];

export type ReceivingCustomer = Pick<UserGroupRecordListMinimal, 'id' | 'name'>;

type Props = {
  fromUserGroupId: number;
  /** Prefills the receiving customer, e.g. the tire hotel customer an issue names. */
  receivingCustomer?: ReceivingCustomer;
  /** The cars' open recalls move too: listed only when there are any. */
  hasOpenRecalls: boolean;
  /** The cars' tire hotel or stored wheels move too: listed only when there are any. */
  hasTireHotelOrStoredWheels: boolean;
  onSubmit: (values: OwnerCorrectionValues) => Promise<unknown>;
};

export const CorrectOwnerAssignmentForm = ({
  fromUserGroupId,
  receivingCustomer,
  hasOpenRecalls,
  hasTireHotelOrStoredWheels,
  onSubmit
}: Props) => {
  const effects: Effect[] = [
    OWNER_EFFECT,
    ...(hasOpenRecalls ? [RECALLS_EFFECT] : []),
    ...(hasTireHotelOrStoredWheels ? [TIRE_HOTEL_EFFECT] : []),
    NOTHING_SENT_EFFECT
  ];
  const defaultValues: OwnerCorrectionValues = {
    customer: receivingCustomer ? { value: receivingCustomer.id, label: receivingCustomer.name } : undefined,
    driverId: 0,
    endReason: undefined,
    reason: ''
  };
  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: ownerCorrectionSchema },
    onSubmit: async ({ value }) => {
      if (value.customer?.value === fromUserGroupId) {
        form.setFieldMeta('customer', (meta) => ({ ...meta, errorMap: { onSubmit: 'Choose a different customer.' } }));
        return;
      }
      if (!recipient.canContinue) {
        form.setFieldMeta('driverId', (meta) => ({
          ...meta,
          errorMap: { onSubmit: 'Select a current member of the receiving customer.' }
        }));
        return;
      }
      await onSubmit(value);
    }
  });
  const values = useStore(form.store, (state) => state.values);
  const selectDriver = (memberId: number) => form.setFieldValue('driverId', memberId);
  const recipient = useOwnershipRecipient(values.customer?.value, values.driverId, selectDriver);
  return (
    <form.AppForm>
      <form
        id={FORM_ID}
        className='flex flex-col gap-4'
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <NoddiInfoBanner
          variant='warning'
          title='Nothing is sold or charged'
          texts={[
            { value: 'Use this when the car is registered on the wrong customer, or an old owner was never removed.' }
          ]}
        />
        <NavioListCard
          title='What happens'
          size='small'
          hideCount
          items={effects}
          getItemKey={(effect) => effect.title}
          renderItem={(effect) => (
            <>
              <NoddiIcon name={effect.icon} size='small' className='shrink-0 text-muted-foreground' />
              <div className='flex min-w-0 flex-1 flex-col'>
                <span className='font-medium'>{effect.title}</span>
                <span className='text-muted-foreground'>{effect.description}</span>
              </div>
            </>
          )}
        />
        <form.AppField name='customer'>
          {() => (
            <TanstackUserGroupAsyncSelect
              label='Receiving customer'
              onSelectionSideEffect={() => form.setFieldValue('driverId', 0)}
            />
          )}
        </form.AppField>
        {recipient.error ? (
          <ApiErrorMessageInternal error={recipient.error} />
        ) : (
          <form.AppField name='driverId'>{() => <OwnershipDriverField recipient={recipient} />}</form.AppField>
        )}
        {values.customer && !recipient.isPending && !recipient.error && recipient.options.length === 0 && (
          <p className='text-sm text-muted-foreground'>Add a member to the receiving customer before correcting.</p>
        )}
        <form.AppField name='endReason'>
          {() => <OwnershipTransferChoices label='Why are the other owners removed?' options={END_REASON_OPTIONS} />}
        </form.AppField>
        <form.AppField name='reason'>
          {(field) => <field.TextArea label='Audit reason' maxLength={255} />}
        </form.AppField>
        <NavioOverlayActions>
          <form.SubscribeButton formId={FORM_ID} label='Correct owner assignment' className='w-full' />
        </NavioOverlayActions>
      </form>
    </form.AppForm>
  );
};
